import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Flex, Image, Stack } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'

import { isApiError } from '@/types/apiErrors.types'

import { DashboardRoutePathsEnum } from '@/modules/dashboard/routes/dashboardRouteConfig.types'
import { getOnboardingDefaultValues } from '@/modules/onboarding/pages/Onboarding.values'

import { useUserStore } from '@/store/user.store'

import { Button } from '@/components/Button/Button'
import { Disclaimer } from '@/components/Disclaimer/Disclaimer'
import { Progress } from '@/components/Progress/Progress'
import { Text } from '@/components/Text/Text'
import { Title } from '@/components/Title/Title'

import { FORM_MESSAGES_MAP } from '@/constants/formMessages.constants'

import { useOnboardingServiceOnboardingControllerOnboarding } from '@/__generated__/queries'
import type { Answer } from '@/__generated__/requests'

import { useLogout } from '@/hooks/useLogout.hook'

import { OnboardingForm } from '../components/OnboardingForm'
import { onboardingFormSchema } from '../components/OnboardingForm.schema'
import type { OnboardingFormSchema } from '../components/OnboardingForm.types'
import { onboardingFormDefaultValues } from '../components/OnboardingForm.values'
import { steps } from '../source/steps'
import classes from './Onboarding.module.css'

export function Component() {
  const stepsCount = steps.length
  const [currentStep, setCurrentStep] = useState(0)
  const [answers, setAnswers] = useState<Answer[]>(getOnboardingDefaultValues())

  const navigate = useNavigate()
  const setUser = useUserStore.use.setUser()

  const logout = useLogout()

  const getProgressValue = useCallback(() => {
    switch (currentStep) {
      case 0:
        return 2
      default:
        return (currentStep / stepsCount) * 100
    }
  }, [currentStep, stepsCount])

  const form = useForm<OnboardingFormSchema>({
    initialValues: onboardingFormDefaultValues,
    validate: zodResolver(onboardingFormSchema),
  })

  const onboardingMutation = useOnboardingServiceOnboardingControllerOnboarding(
    {
      onError: error => {
        let errorMessage = FORM_MESSAGES_MAP.UNKNOWN_ERROR

        if (isApiError(error)) {
          errorMessage = error.body.message || errorMessage
        }

        notifications.show({
          color: 'red',
          message: errorMessage,
          title: 'Failed to send the onboarding form',
        })
      },
      onSuccess: data => {
        setUser(data)

        modals.open({
          centered: true,
          children: (
            <Stack p={20}>
              <Image
                alt="Rolan Wealth"
                className={classes.contentLogo}
                h="auto"
                src="success.png"
                w={80}
              />
              <Title ta="center" variant="h2">
                Great, you’re done!
              </Title>
              <Text ta="center" variant="body_2">
                Step into your financial future as we guide you to your
                personalized dashboard, filled with insights and resources
                tailored just for you.
              </Text>
            </Stack>
          ),
        })
        navigate(DashboardRoutePathsEnum.DASHBOARD)
      },
    },
  )

  const finishStep = (stepAnswers: Answer[]) => {
    let newAnswers = [...answers]

    stepAnswers.forEach(stepAnswer => {
      newAnswers = newAnswers.map(answer => {
        if (answer.fieldName === stepAnswer.fieldName) return stepAnswer

        return answer
      })
    })

    let stepHasErrors = false

    steps[currentStep].fields.forEach(field => {
      if (form.validateField(field.field_name).hasError) {
        stepHasErrors = true
      }
    })

    if (!stepHasErrors) {
      if (currentStep < stepsCount - 1) {
        setCurrentStep(currentStep + 1)
      } else {
        onboardingMutation.mutate({
          requestBody: {
            answers: [...newAnswers],
          },
        })
      }
    }

    setAnswers(newAnswers)
  }

  const previousStep = (stepAnswers: Answer[]) => {
    let newAnswers = [...answers]

    stepAnswers.forEach(stepAnswer => {
      newAnswers = newAnswers.map(answer => {
        if (answer.fieldName === stepAnswer.fieldName) return stepAnswer

        return answer
      })
    })

    setAnswers(newAnswers)
    setCurrentStep(currentStep - 1)
  }

  return (
    <Flex className={classes.content}>
      <Progress value={getProgressValue()} />

      <Image
        alt="Rolan Wealth"
        className={classes.contentLogo}
        src="logo.svg"
      />

      <Disclaimer
        description={
          <>
            Your financial information is private and will never be shared with
            your employer or any third party. If you choose to speak with a
            CFP®, your information is shared with them only with your
            permission. This helps them provide more personalized guidance
            during your conversation.
            <br />
            <br />
            Completing this form is not required to explore the platform, but it
            must be completed before booking time with a CFP®.
          </>
        }
        title="Disclaimer information"
      />

      <OnboardingForm
        key={currentStep}
        currentStep={currentStep}
        finishStep={finishStep}
        hideDisclaimer
        isFinal={currentStep === stepsCount - 1}
        prevAnswers={answers}
        previousStep={previousStep}
        step={steps[currentStep]}
      />

      <Box className={classes.logoutButton}>
        <Button miw={0} onClick={logout} type="button" variant="text" w="auto">
          Log out
        </Button>
      </Box>
    </Flex>
  )
}

Component.displayName = 'Onboarding'
